import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import avatarImage from "@/assets/images/avatar.svg";

import { fetchCaseSummary } from "../../api/case";
import { addClickRecord, getCaseId, getClientId } from "../../Utils/helper";
import { fetchCurrentCase, setCaseSummary } from "../../Redux/caseData/caseDataSlice";
import { api_without_cancellation } from "../../api/api";
import { updateCoCounselPositions } from "../../api/coCounsel";
import NewPhoneIcon from "../../assets/NewPhoneIcon";
import { useTelephonySettings } from "../../Hooks/useGetTelephonySettings";
import CallsIcon from "../../assets/call-icon";
import useGetTeams from "../FirmSettings/add-team-tab/hooks/useTeamTabApi";

const toFormUser = (fu) => ({
  id: fu?.id || "",
  user: {
    first_name: fu?.user?.first_name || "",
    last_name: fu?.user?.last_name || "",
  },
  avatar: fu?.profile_pic_19p || "",
});

const Avatar = ({ src }) =>
  src ? (
    <div className="ic ic-avatar ic-19 has-avatar-icon has-cover-img">
      <img src={src} className="img-user" alt="" />
    </div>
  ) : (
    <span className="ic ic-avatar ic-19 has-avatar-icon has-cover-img">
      <span className="img-user" />
    </span>
  );

// Codebase-standard dropdown: custom-select-new-provider button + dropdown-menu panel.
// Arrow is drawn by the .custom-select-new-provider::after CSS rule.
const CustomDropdown = ({ isOpen, onToggle, avatar, label, children, minWidth = 230 }) => (
  <div className="custom-select-new-provider position-relative" style={{ minWidth }}>
    <div
      className="dropdown-button form-control pt-0 pb-0 pr-0 rounded-0 height-25 d-flex align-items-center"
      style={{ paddingLeft: "5px", backgroundColor: "white", cursor: "pointer", gap: "5px" }}
      onClick={onToggle}
      role="button"
      tabIndex={0}
    >
      {avatar !== undefined && <Avatar src={avatar} />}
      <div
        style={{
          height: "25px",
          lineHeight: "25px",
          fontWeight: 600,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </div>
    </div>
    {isOpen && (
      <div
        className="dropdown-menu show w-100 p-0"
        style={{
          position: "absolute",
          top: "100%",
          left: 0,
          zIndex: 10,
          maxHeight: 220,
          overflowY: "auto",
          border: "1px solid #ccc",
        }}
      >
        {children}
      </div>
    )}
  </div>
);

const DropdownOption = ({ avatar, children, onClick }) => (
  <div
    className="dropdown-option dropdown-option-case-worker d-flex height-25 align-items-center"
    style={{ padding: "0px 5px", cursor: "pointer", gap: "5px", border: "1px solid #eee" }}
    onClick={onClick}
    role="button"
    tabIndex={-1}
  >
    {avatar !== undefined && <Avatar src={avatar || avatarImage} />}
    <div style={{ fontWeight: 600 }}>{children}</div>
  </div>
);

const AttachUserModal = ({ isModalOpen, closeModal }) => {
  const firmRoles = useSelector((state) => state?.caseData?.summary?.firm_roles);
  const dispatch = useDispatch();
  const currentCase = useSelector((state) => state?.caseData?.current);
  const caseSummary = useSelector((state) => state.caseData?.summary);
  const page_id_click_record = useSelector(
    (state) => state.page_id_click_record.page_id_click_record
  );
  const [workers, setWorkers] = useState([]);
  // single source of truth for which dropdown is open: "team" | "user1".."user6" | null
  const [openKey, setOpenKey] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const { selectedRoleIds } = useTelephonySettings();
  const { data: teamsData } = useGetTeams();
  // firm_user1..6 / staff_team / workers in the summary are always the viewer's own firm's slots
  const isCoCounsel = caseSummary?.team_scope?.role === "cocounsel";
  const cocounselId = caseSummary?.team_scope?.cocounsel_id;

  const teams = useMemo(
    () => (Array.isArray(teamsData?.teams_data) ? teamsData.teams_data : []),
    [teamsData]
  );
  const selectedTeam = useMemo(
    () => teams.find((t) => t.id === selectedTeamId) || null,
    [teams, selectedTeamId]
  );

  // firm_user ids belonging to the selected team, across all 6 slots
  const selectedTeamMemberIds = useMemo(() => {
    if (!selectedTeam) return new Set();
    const ids = new Set();
    for (let i = 1; i <= 6; i++) {
      const id = selectedTeam[`firm_user${i}`]?.id;
      if (id) ids.add(id);
    }
    return ids;
  }, [selectedTeam]);

  const toggle = (key) => {
    setOpenKey((prev) => (prev === key ? null : key));
    addClickRecord(999, page_id_click_record);
  };

  useEffect(() => {
    if (currentCase && caseSummary?.workers) {
      setWorkers([
        { firm_user: caseSummary?.firm_user1, user_type: caseSummary.workers[0] },
        { firm_user: caseSummary?.firm_user2, user_type: caseSummary.workers[1] },
        { firm_user: caseSummary?.firm_user3, user_type: caseSummary.workers[2] },
        { firm_user: caseSummary?.firm_user4, user_type: caseSummary.workers[3] },
        { firm_user: caseSummary?.firm_user5, user_type: caseSummary.workers[4] },
        { firm_user: caseSummary?.firm_user6, user_type: caseSummary.workers[5] },
      ]);
    }
  }, [currentCase, caseSummary?.workers, caseSummary]);

  useEffect(() => {
    setSelectedTeamId(caseSummary?.staff_team?.id || null);
  }, [caseSummary?.staff_team]);

  const formik = useFormik({
    initialValues: {
      firm_user1: toFormUser(workers[0]?.firm_user),
      firm_user2: toFormUser(workers[1]?.firm_user),
      firm_user3: toFormUser(workers[2]?.firm_user),
      firm_user4: toFormUser(workers[3]?.firm_user),
      firm_user5: toFormUser(workers[4]?.firm_user),
      firm_user6: toFormUser(workers[5]?.firm_user),
    },
    onSubmit: async (values) => {
      setIsPending(true);
      const workerIds = Object.keys(values).reduce((acc, key) => {
        acc[key] = values[key].id;
        return acc;
      }, {});
      const caseID = getCaseId();
      addClickRecord(999, page_id_click_record);
      try {
        if (isCoCounsel) {
          // co-counsel firms save their own six slots on the co-counsel relationship;
          // case-update rejects firm_userN from them with positions_owner_only
          const positions = {};
          for (let i = 1; i <= 6; i++) positions[i] = workerIds[`firm_user${i}`] || null;
          await updateCoCounselPositions(cocounselId, positions);
        } else {
          await api_without_cancellation.post("/api/case-update/", {
            ...workerIds,
            case_id: caseID,
            staff_team_id: selectedTeamId,
          });
        }
        dispatch(setCaseSummary(null));
        dispatch(fetchCurrentCase(getClientId(), getCaseId()));
        const data = await fetchCaseSummary(getClientId(), getCaseId());
        dispatch(setCaseSummary(data));
        closeModal();
      } catch (error) {
        toast.error(error?.response?.data?.error || "Could not save case workers.");
      } finally {
        setIsPending(false);
      }
    },
    enableReinitialize: true,
  });

  // team picked → fill all six slots from the preset
  const handleTeamSelect = (team) => {
    setSelectedTeamId(team.id);
    for (let i = 1; i <= 6; i++) {
      formik.setFieldValue(`firm_user${i}`, toFormUser(team[`firm_user${i}`]));
    }
    setOpenKey(null);
    addClickRecord(999, page_id_click_record);
  };

  // manual role change → detach team if the picked user isn't a team member
  const handleUserSelect = (slot, role) => {
    formik.setFieldValue(`firm_user${slot}`, {
      id: role.for_firm_user.id,
      user: {
        first_name: role.for_firm_user.user.first_name,
        last_name: role.for_firm_user.user.last_name,
      },
      avatar: role.for_firm_user.profile_pic_19p,
    });
    if (selectedTeamId && !selectedTeamMemberIds.has(role.for_firm_user.id)) {
      setSelectedTeamId(null);
    }
    setOpenKey(null);
    addClickRecord(999, page_id_click_record);
  };

  // one handler closes whichever dropdown is open when clicking outside any of them
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".custom-select-new-provider")) setOpenKey(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const roleOptionsFor = (slot) => {
    const expectedRole = workers[slot]?.user_type?.name;
    const activeRoles = firmRoles?.filter((role) => role?.for_firm_user?.is_active === true);
    const matchingRoles = activeRoles?.filter((role) =>
      role?.for_firm_usertype?.some((type) => type?.name === expectedRole)
    );
    // eslint-disable-next-line no-console -- Temporary dropdown diagnostics.

    return matchingRoles?.map((role) => (
      <DropdownOption
        key={role.id}
        avatar={role.for_firm_user.profile_pic_19p}
        onClick={() => handleUserSelect(slot + 1, role)}
      >
        {role.for_firm_user.user.first_name} {role.for_firm_user.user.last_name}
      </DropdownOption>
    ));
  };

  const PhoneCell = ({ slot }) => (
    <td style={{ width: "25px", maxWidth: "25px", minWidth: "25px" }}>
      <span className="d-flex align-items-center justify-content-center">
        {selectedRoleIds?.has(workers[slot]?.user_type?.id) ? (
          <NewPhoneIcon />
        ) : (
          <CallsIcon colorCode="var(--primary-25)" />
        )}
      </span>
    </td>
  );

  const WorkerSlot = ({ slot }) => {
    const value = formik.values[`firm_user${slot + 1}`];
    return (
      <CustomDropdown
        isOpen={openKey === `user${slot + 1}`}
        onToggle={() => toggle(`user${slot + 1}`)}
        avatar={value.avatar}
        label={`${value.user.first_name} ${value.user.last_name}`.trim()}
      >
        {roleOptionsFor(slot)}
      </CustomDropdown>
    );
  };

  return (
    <Modal
      show={isModalOpen}
      onHide={() => closeModal()}
      id="attach-user-modal"
      dialogClassName="max-750"
      size="md"
      centered
    >
      <div className="modal-header p-0 height-25 bg-primary justify-content-center">
        <h5
          className="modal-title height-25 d-flex align-items-center justify-content-center text-white"
          id="exampleModalLabel"
        >
          Attach and Edit Case Workers Assigned to Case
        </h5>
      </div>

      <div className="modal-body p-0">
        {/* Team selector (owner only: the co-counsel positions endpoint has no staff team) */}
        {!isCoCounsel && (
          <div className="d-flex align-items-center m-l-5 m-r-5 m-t-5">
            <span
              className="text-start"
              style={{
                minWidth: "180px",
                fontWeight: 600,
                color: "var(--primary-25)",
                paddingLeft: "5px",
              }}
            >
              Team:
            </span>
            <div className="d-flex-1" style={{ minWidth: "230px" }}>
              <CustomDropdown
                isOpen={openKey === "team"}
                onToggle={() => toggle("team")}
                label={selectedTeam?.team_name || "No Team Selected"}
              >
                {teams.map((team) => (
                  <DropdownOption key={team.id} onClick={() => handleTeamSelect(team)}>
                    {team.team_name}
                  </DropdownOption>
                ))}
              </CustomDropdown>
            </div>
          </div>
        )}

        <table className="">
          <tbody className="d-flex flex-column m-t-5" style={{ gap: "5px" }}>
            {workers.slice(0, 3).map((_, index) => (
              <tr className="m-l-5 height-25 m-r-5" key={index}>
                <PhoneCell slot={index} />
                <td
                  className="ps-0 p-r-5 text-start td-autosize"
                  style={{ minWidth: "155px", fontWeight: 600, color: "var(--primary-25)" }}
                >
                  {workers[index]?.user_type?.name}:
                </td>
                <td className="d-flex-1" style={{ minWidth: "239.5px" }}>
                  <WorkerSlot slot={index} />
                </td>

                <PhoneCell slot={index + 3} />
                <td
                  className="text-start ps-0 p-r-5 td-autosize"
                  style={{ minWidth: "155px", fontWeight: 600, color: "var(--primary-25)" }}
                >
                  {workers[index + 3]?.user_type?.name}:
                </td>
                <td className="d-flex-1" style={{ minWidth: "239.5px" }}>
                  <WorkerSlot slot={index + 3} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="p-0 mt-0 padding-outside-btn-new-provider modal-footer"
        style={{ borderTop: "none" }}
      >
        <button
          type="button"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center btn btn-secondary"
          onClick={() => closeModal()}
          id="attach-user-modal-close"
        >
          Close
        </button>
        <button
          type="button"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center btn btn-success"
          onClick={formik.handleSubmit}
          disabled={isPending}
        >
          {isPending ? (
            <div className="loader loader-small" />
          ) : (
            <>Save Changes to Case Workers Assigned to Case</>
          )}
        </button>
      </div>
    </Modal>
  );
};

export default AttachUserModal;
